import moment from 'moment';

// =============================================================================
// dateFormat — governed moment format helpers for v2/v3.1.
//
// Four tiers matching the v3.1 `dateFormat.*` contract (see
// `ux/Tasks/configs/designTokens.js:351`). Use these helpers instead of
// hand-writing `moment(x).format('MMM DD, YYYY')` in consumer code so the
// date presentation stays consistent across Campaign Details, Tasks, and
// every future v2 route.
//
//   formatFull      — "Jan 05, 2025 2:30 PM"   · timestamps, activity logs
//   formatShort     — "Jan 05 2:30 PM"         · recent activity, compact timestamps
//   formatDateOnly  — "Jan 05, 2025"           · milestones, due dates, project dates
//   formatCompact   — "01/05"                  · dense tables, sparkline labels, axis ticks
//
// Each helper accepts any moment-parseable input (Date, string, Moment) and
// returns an empty string for null/undefined/invalid input. The empty-string
// fallback is intentional — avoids `Invalid date` placeholders in the UI.
// =============================================================================

export const DATE_FORMAT = {
  full: 'MMM DD, YYYY h:mm A',
  short: 'MMM DD h:mm A',
  dateOnly: 'MMM DD, YYYY',
  compact: 'MM/DD',
};

const safeMoment = (input) => {
  if (input === null || input === undefined) return null;
  const m = moment(input);
  return m.isValid() ? m : null;
};

export const formatFull = (input, fallback = '') => {
  const m = safeMoment(input);
  return m ? m.format(DATE_FORMAT.full) : fallback;
};

export const formatShort = (input, fallback = '') => {
  const m = safeMoment(input);
  return m ? m.format(DATE_FORMAT.short) : fallback;
};

export const formatDateOnly = (input, fallback = '') => {
  const m = safeMoment(input);
  return m ? m.format(DATE_FORMAT.dateOnly) : fallback;
};

export const formatCompact = (input, fallback = '') => {
  const m = safeMoment(input);
  return m ? m.format(DATE_FORMAT.compact) : fallback;
};

// Campaign flight dates specifically. They are stored UTC-anchored — a start
// as `2026-06-01T00:00:00.000Z`, an end as `2026-07-31T23:59:59.999Z` — while
// `formatDateOnly` parses in the browser's zone. Rendering them raw therefore
// shifts the day for anyone off UTC: a PH viewer (UTC+8) reads the Jul 31 end
// as "Aug 01", an LA viewer (UTC-7) reads the Jun 1 start as "May 31".
//
// Anchoring to a bare UTC day-string before formatting makes the output the
// same for every viewer, and matches the UTC buckets the charts and pickers
// use (see project_summary_utc_pin). Use this for every flight-date render —
// the incantation was previously copy-pasted, and four of the five flight
// cards had dropped it.
export const formatFlightDate = (input, fallback = '') => {
  if (input === null || input === undefined || input === '') return fallback;
  // Explicit formats for strings — the lenient constructor warns on unknown
  // input, which would fire on every render of a malformed date.
  const utc =
    typeof input === 'string'
      ? moment.utc(input, [moment.ISO_8601, 'YYYY-MM-DD'], false)
      : moment.utc(input);
  if (!utc.isValid()) return fallback;
  return formatDateOnly(utc.format('YYYY-MM-DD'), fallback);
};

// Escape hatch for one-off formats that aren't on the governed ladder.
// Prefer the named helpers above; use this only when a specific tier doesn't
// fit (e.g. 24-hour columns with `HH:mm:ss` that require second precision).
export const formatCustom = (input, pattern, fallback = '') => {
  const m = safeMoment(input);
  return m ? m.format(pattern) : fallback;
};
